import {
	Component,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { Subscription } from 'rxjs';

import { ITimeMarker } from 'vbrick-player-src/videogular/controls/VbTimeMarkers.Component';
import { VideogularComponent } from 'vbrick-player-src/videogular/Videogular.Component';
import { VBrickPlayerComponent } from 'vbrick-player-src/VbrickPlayer.Component';

import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { VideoService } from 'rev-shared/media/Video.Service';
import { IVbVideoPlayerConfig } from 'rev-shared/videoPlayer/IVbVideoPlayerConfig';
import { getChapterMarkers } from 'rev-shared/videoPlayer/MarkerUtils';
import { isNumber } from 'rev-shared/util';
import { parseUrlTimespan } from 'rev-shared/date/DateParsers.Service';

import './embed-video.less';
import { EmbedPlayerMessageBus } from 'rev-shared/embed/EmbedPlayerMessageBus.Service';

@Component({
	selector: 'embed-video',
	templateUrl: './EmbedVideo.Component.html'
})
export class EmbedVideo implements OnInit, OnDestroy {
	@Input() public accountLicense: AccountLicenseService;
	@Input() public params: any;
	@Input() public playlist: any;
	@Input() public video: any;

	public autoplay: boolean;
	public startAt: number;
	public playInLoop: boolean;
	public hideChapters: boolean;
	public playerConfig: IVbVideoPlayerConfig;
	public chapterImagesToggle: boolean;
	public isVideoUnavailable: boolean;
	public markers: ITimeMarker[];
	public vgAPI: VideogularComponent;
	private subscription: Subscription;

	protected preservedVolume: number;
	constructor(
		private VideoService: VideoService,
		private EmbedPlayerMessageBus: EmbedPlayerMessageBus
	) {}

	public ngOnInit(): void {
		this.shapeEmbedParams();
		this.isVideoUnavailable = false;

		this.updateChapterMarkers();

		this.EmbedPlayerMessageBus.onVodLoaded(this.video);
	}

	public ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

	private shapeEmbedParams(): void {
		if (!this.params) {
			return;
		}

		this.autoplay = this.params.autoplay;
		this.startAt = parseUrlTimespan(this.params.startAt);
		this.playInLoop = this.params.loopVideo;
		this.hideChapters = this.params.noChapters;
		this.playerConfig = {
			playerControls: {
				overlayControls: !this.params.noCenterButtons,
				playControls: !this.params.noPlayBar,
				cc: !this.params.noCc,
				settings: !this.params.noSettings,
				fullscreen: !this.params.noFullscreen
			},
			accentColor: this.params.accent,
			forcedCCOn: this.params.forceClosedCaptions,
			subtitleLangPreferenceOrder: BootstrapContext.languagePreferenceOrder
				.map(lang => lang.toLowerCase())
		};
	}

	public onPlayerReady(vgAPI: VideogularComponent, player: VBrickPlayerComponent): void {
		this.vgAPI = vgAPI;

		if (isNumber(this.preservedVolume)) {
			vgAPI.setVolume(this.preservedVolume);
		}

		this.subscription = this.EmbedPlayerMessageBus.handlePlayerEvents(player, vgAPI).subscribe();
	}

	public onPlaylistSelectionChanged({ videoId }: {videoId: string}): void {
		this.loadVideo(videoId);
	}

	public onPlaybackEnded(): void {
		if (this.playInLoop) {
			window.setTimeout(() => this.vgAPI.play(), 500);
			return;
		}

		this.playNextVideoInPlaylist();
	}

	public playNextVideoInPlaylist(): void {
		if (!this.playlist) {
			return;
		}

		const nextIndex: number = this.getNextVideoIndex(this.getCurrentVideoIndex());

		this.isVideoUnavailable = false;

		window.setTimeout(() => this.loadVideo(this.playlist.videos[nextIndex].id), 500);
	}

	private getCurrentVideoIndex(): number {
		return this.playlist.videos.findIndex((video: any) => video.id === this.video.id);
	}

	private getNextVideoIndex(currentVideoIndex: number): number {
		return currentVideoIndex === this.playlist.videos.length - 1 ? 0 : currentVideoIndex + 1;
	}

	protected loadVideo(videoId: string): void {
		if (videoId === this.video.id) {
			return;
		}

		this.autoplay = true;

		if (this.vgAPI && isNumber(this.vgAPI.volume)) {
			this.preservedVolume = this.vgAPI.volume;
		}

		this.VideoService.getVideoPlaybackEmbed(videoId)
			.then((video: any) => this.onLoadVideoSuccess(video))
			.catch(() => this.onLoadVideoError(videoId));
	}

	protected onLoadVideoSuccess(video: any): void {
		this.video = video;
		this.isVideoUnavailable = false;
		this.updateChapterMarkers();
	}

	protected onLoadVideoError(videoId: string): void {
		this.video = { id: videoId };
		this.isVideoUnavailable = true;
		this.updateChapterMarkers();
	}

	protected updateChapterMarkers(): void {
		if (this.hideChapters) {
			return;
		}
		this.markers = getChapterMarkers(this.video);
	}
}
